// Rhythm Echo rules (issue #3473, pacing revamped in #3519): the rhythm vocabulary and patterns,
// pattern generation, onset timing and the per-pattern timeline, tap-to-beat scoring with headline
// and hint copy, session score and best streak, and the warm-up's tap-offset arithmetic. Pure
// functions and plain data shared by the exercise state machine, the audio session, the notation
// components and the stories. No DOM, no audio. Tunables (bpm, on-time window, patterns per session) live in
// learn_rules.ts.

import { learnSessionScoreMax, rhythmLevelParams, rhythmPatternsPerSession } from './learn_rules.ts';
import { LearnLevel } from './model.ts';

// ---- Vocabulary ------------------------------------------------------------------------------

export const rhythmBeatsPerBar = 4; // every pattern is one bar of 4/4

export type NoteValue = 'whole' | 'half' | 'quarter' | 'eighth' | 'sixteenth';

export const noteValueBeats: Record<NoteValue, number> = {
  whole: 4,
  half: 2,
  quarter: 1,
  eighth: 0.5,
  sixteenth: 0.25,
};

// What the notation row draws for one event: `count` notes of `value`, beamed together when more
// than one, or a single rest glyph of `value`.
export interface RhythmNotation {
  value: NoteValue;
  count: number;
  beamed: boolean;
  rest: boolean;
}

// One vocabulary item, e.g. ta (a quarter) or ti-ti (two beamed eighths). `onsets` are the offsets
// of each sounded note within the event, in beats from the event's start; a rest has none.
// `syllables` has one entry per onset so the blocks can light each syllable in time.
export interface RhythmEvent {
  name: string; // 'ta', 'ti-ti', 'rest', ...
  beats: number;
  onsets: number[];
  syllables: string[];
  notation: RhythmNotation;
}

export type RhythmPattern = RhythmEvent[]; // events summing to rhythmBeatsPerBar beats

function notes(name: string, value: NoteValue, syllables: string[]): RhythmEvent {
  const count = syllables.length;
  const noteBeats = noteValueBeats[value];
  return {
    name,
    beats: noteBeats * count,
    onsets: syllables.map((_syllable, index) => index * noteBeats),
    syllables,
    notation: { value, count, beamed: count > 1, rest: false },
  };
}

function rest(name: string, value: NoteValue): RhythmEvent {
  return {
    name,
    beats: noteValueBeats[value],
    onsets: [],
    syllables: [],
    notation: { value, count: 1, beamed: false, rest: true },
  };
}

export const ta = notes('ta', 'quarter', ['ta']);
export const tiTi = notes('ti-ti', 'eighth', ['ti', 'ti']);
export const quarterRest = rest('rest', 'quarter');
export const taA = notes('ta-a', 'half', ['ta-a']); // Medium/Hard slice
export const tikaTika = notes('tika-tika', 'sixteenth', ['ti', 'ka', 'ti', 'ka']); // Medium/Hard slice

// The events a level's patterns are drawn from. Easy is ta and ti-ti only: no rests, which Michael
// found too hard for the first level (feedback of 2026-09-11). Medium adds the quarter rest; Hard
// reuses the Medium vocabulary until the Medium/Hard slice adds ta-a and tika-tika. Only Easy is
// reachable in the app until then.
const easyVocabulary: RhythmEvent[] = [ta, tiTi];
const mediumVocabulary: RhythmEvent[] = [ta, tiTi, quarterRest];

export const rhythmVocabulary: Record<LearnLevel, RhythmEvent[]> = {
  [LearnLevel.Easy]: easyVocabulary,
  [LearnLevel.Medium]: mediumVocabulary,
  [LearnLevel.Hard]: mediumVocabulary,
};

// "ta ta ti-ti ta"; a rest reads as "rest"
export function patternSyllables(pattern: RhythmPattern): string {
  return pattern.map((event) => event.name).join(' ');
}

export function patternBeats(pattern: RhythmPattern): number {
  return pattern.reduce((total, event) => total + event.beats, 0);
}

// ---- Generation ------------------------------------------------------------------------------

export type RandomSource = () => number; // uniform in [0, 1), like Math.random

function pick<T>(items: T[], random: RandomSource): T {
  return items[Math.min(items.length - 1, Math.floor(random() * items.length))];
}

// Draws events at random from the level's vocabulary until the bar is full. A rest that would
// complete a bar with no onsets is never drawn, so a bar is never all rests (Easy has no rests at
// all; the rule matters from Medium on).
export function generatePattern(level: LearnLevel, random: RandomSource = Math.random): RhythmPattern {
  const vocabulary = rhythmVocabulary[level];
  const pattern: RhythmPattern = [];
  let remaining = rhythmBeatsPerBar;
  let hasOnset = false;
  while (remaining > 0) {
    const candidates = vocabulary.filter(
      (event) => event.beats <= remaining && (hasOnset || event.onsets.length > 0 || event.beats < remaining),
    );
    const event = pick(candidates, random);
    pattern.push(event);
    remaining -= event.beats;
    hasOnset ||= event.onsets.length > 0;
  }
  return pattern;
}

export function generateSession(level: LearnLevel, random: RandomSource = Math.random): RhythmPattern[] {
  return Array.from({ length: rhythmPatternsPerSession }, () => generatePattern(level, random));
}

// ---- Timing ----------------------------------------------------------------------------------

export function beatMs(bpm: number): number {
  return 60000 / bpm;
}

// Onsets in beats from the bar start, in order
export function patternOnsetBeats(pattern: RhythmPattern): number[] {
  const onsets: number[] = [];
  let position = 0;
  for (const event of pattern) {
    onsets.push(...event.onsets.map((offset) => position + offset));
    position += event.beats;
  }
  return onsets;
}

// Onsets in ms from the bar start
export function patternOnsetsMs(pattern: RhythmPattern, bpm: number): number[] {
  return patternOnsetBeats(pattern).map((beat) => beat * beatMs(bpm));
}

// The shortest sounded note a level can ask for, in beats (rests do not count)
export function shortestNoteBeats(level: LearnLevel): number {
  return Math.min(
    ...rhythmVocabulary[level]
      .filter((event) => !event.notation.rest)
      .map((event) => noteValueBeats[event.notation.value]),
  );
}

// Each onset owns a band of this much on either side; a tap inside it belongs to that onset. Half
// the shortest note, so neighbouring bands never overlap (Easy: half an eighth, about 214 ms).
export function matchBandMs(level: LearnLevel): number {
  return (shortestNoteBeats(level) / 2) * beatMs(rhythmLevelParams[level].bpm);
}

// Everything the exercise schedules for one pattern, in ms on whatever clock `startMs` is on.
// `startMs` is the moment of the button tap (Start / Next / Try again / Play again); from there the
// pattern runs: one silent lead beat, the play bar (the pattern sounds), then straight away the tap
// bar, whose first beat is Go! (issue #3588). Ready and Set pulse on the play bar's third and fourth
// beats, over whatever the pattern sounds there, exactly as the warm-up does over its listen bar.
// Nothing clicks anywhere - the Ready · Set · Go! cue is purely visual (issue #3519), so the only
// sounds are the pattern's own onsets and the percussion the page plays under each accepted tap.
// The audio session plays the onsets at these times; the state machine switches from listening to
// tapping at the tap bar start and opens and closes tapping at `tapsOpenMs` and `tapsEndMs`.
export interface RhythmTimeline {
  startMs: number;
  beatMs: number;
  bandMs: number; // see matchBandMs
  windowMs: number; // the level's on-time window
  playBarStartMs: number; // startMs + one beat: exactly one silent beat of lead
  playOnsetsMs: number[]; // the pattern played for the singer to hear
  // The beats the Ready · Set · Go! words pulse on: `ready` and `set` are the play bar's third and
  // fourth beats, `go` the tap bar's first. Whole beats from the bar start, so a subdivided beat
  // never moves a pulse. See learn_cue.ts (client) for the pulse maths.
  cueMs: { ready: number; set: number; go: number };
  tapBarStartMs: number; // cueMs.go, the beat right after the play bar: the first tap belongs on Go!
  tapOnsetsMs: number[]; // where the taps are expected
  // cueMs.set: taps are accepted from Set, a full beat before Go!, to catch a keen singer. Earlier
  // taps are ignored.
  tapsOpenMs: number;
  tapsEndMs: number; // the tap bar's end plus the band; the tapping phase ends here
}

export function patternTimeline(pattern: RhythmPattern, level: LearnLevel, startMs: number): RhythmTimeline {
  const beat = beatMs(rhythmLevelParams[level].bpm);
  const bar = rhythmBeatsPerBar * beat;
  const bandMs = matchBandMs(level);
  const onsetsMs = patternOnsetsMs(pattern, rhythmLevelParams[level].bpm);
  const playBarStartMs = startMs + beat;
  const tapBarStartMs = playBarStartMs + bar;
  const cueMs = { ready: tapBarStartMs - 2 * beat, set: tapBarStartMs - beat, go: tapBarStartMs };
  return {
    startMs,
    beatMs: beat,
    bandMs,
    windowMs: rhythmLevelParams[level].windowMs,
    playBarStartMs,
    playOnsetsMs: onsetsMs.map((onset) => playBarStartMs + onset),
    cueMs,
    tapBarStartMs,
    tapOnsetsMs: onsetsMs.map((onset) => tapBarStartMs + onset),
    tapsOpenMs: cueMs.set,
    tapsEndMs: tapBarStartMs + bar + bandMs,
  };
}

// The index of the last time in `timesMs` (ascending) at or before `nowMs`, or undefined before the
// first: the onset a pattern has played up to, the click the warm-up has sounded
export function lastReachedIndex(timesMs: number[], nowMs: number): number | undefined {
  let reached: number | undefined;
  for (const [index, timeMs] of timesMs.entries()) {
    if (timeMs > nowMs) {
      break;
    }
    reached = index;
  }
  return reached;
}

// ---- Scoring ---------------------------------------------------------------------------------
// Decisions of 2026-09-10 on issue #3473. A tap inside an onset's band belongs to that onset; a tap
// outside every band belongs to the most recent onset before it (the beat that was sounding, rests
// included); a tap before the first band opens is ignored. An onset with
// exactly one tap in its band and no stray tap is on time within the window and early or late
// otherwise; no tap in its band is a miss; a second tap in the band or any stray tap makes it a
// miss too (an extra tap costs the beat, there is no separate penalty).
//
// Decision of 2026-09-23 on issue #3588: taps are accepted from Set, a full beat before Go!, so the
// first onset's band, and only the first onset's, reaches back to Set: its lower edge is one beat
// before the tap bar start instead of the onset minus the band; its upper edge is unchanged, and
// every other onset keeps its symmetric band. A keen first tap from Set is therefore early (a
// second tap in that band still makes the beat a miss), and with a leading rest a tap between Set
// and the first sounded onset belongs to that onset and is early: tapping in a rest is an error,
// not a free tap. A tap before Set is still ignored (the tap gate drops it anyway). See onsetBandsMs.

export enum BeatStatus {
  OnTime = 'onTime', // green
  Early = 'early', // yellow
  Late = 'late', // yellow
  Missed = 'missed', // red
}

export interface BeatResult {
  onsetMs: number; // expected, from the tap bar start
  status: BeatStatus;
  deltaMs?: number; // tap minus onset for the one tap in the band, when there was exactly one
  extraTaps: number; // taps beyond the first in the band, plus stray taps assigned to this onset
  tapsMs: number[]; // every tap assigned to this onset, from the tap bar start
}

export type ResultColor = 'green' | 'yellow' | 'red';

export interface PatternResult {
  pattern: RhythmPattern;
  beats: BeatResult[]; // one per onset, in order
  onTime: number;
  total: number; // onsets in the pattern
  headline: string; // "Nice, 4 of 5"
  hint: string; // "One tap was a little late."
  color: ResultColor; // green all on time, yellow some on time, red none
  tapsMs: number[]; // the taps that counted, from the tap bar start, for the "what you tapped" row
}

// The stretch of the tap bar a tap must land in to belong to an onset, in ms from the tap bar start,
// edges included
export interface OnsetBand {
  startMs: number;
  endMs: number;
}

// Where the first onset's band opens: on Set, one beat before the tap bar start (see the decision of
// 2026-09-23 above)
export function firstBandStartMs(bpm: number): number {
  return -beatMs(bpm);
}

// One band per onset, in onset order: `bandMs` either side of the onset, except that the first
// onset's band reaches back to firstBandStartMs
export function onsetBandsMs(onsetsMs: number[], bandMs: number, bpm: number): OnsetBand[] {
  return onsetsMs.map((onsetMs, index) => ({
    startMs: index === 0 ? firstBandStartMs(bpm) : onsetMs - bandMs,
    endMs: onsetMs + bandMs,
  }));
}

export function isInBand(tapMs: number, band: OnsetBand): boolean {
  return tapMs >= band.startMs && tapMs <= band.endMs;
}

// Index of the onset a tap belongs to, or undefined for a tap before the first band opens
function assignTap(tapMs: number, onsetsMs: number[], bands: OnsetBand[]): number | undefined {
  let inBand: number | undefined;
  let lastBefore: number | undefined;
  onsetsMs.forEach((onsetMs, index) => {
    const distance = Math.abs(tapMs - onsetMs);
    if (isInBand(tapMs, bands[index]) && (inBand === undefined || distance < Math.abs(tapMs - onsetsMs[inBand]))) {
      inBand = index;
    }
    if (onsetMs <= tapMs) {
      lastBefore = index;
    }
  });
  return inBand ?? lastBefore;
}

function beatResult(onsetMs: number, tapsMs: number[], band: OnsetBand, windowMs: number): BeatResult {
  const inBand = tapsMs.filter((tapMs) => isInBand(tapMs, band));
  const extraTaps = tapsMs.length - Math.min(inBand.length, 1);
  if (inBand.length !== 1 || extraTaps > 0) {
    return { onsetMs, status: BeatStatus.Missed, extraTaps, tapsMs };
  }
  const deltaMs = inBand[0] - onsetMs;
  const status = Math.abs(deltaMs) <= windowMs ? BeatStatus.OnTime : deltaMs < 0 ? BeatStatus.Early : BeatStatus.Late;
  return { onsetMs, status, deltaMs, extraTaps, tapsMs };
}

// Scores one pattern. `tapsMs` are the singer's taps in ms from the tap bar start, already aligned
// through the calibration offset (see alignTap). Stopping early is the same call with the taps so
// far: every onset without a tap is missed.
// `input` only words the hint: a sung onset is scored exactly as a tap is (issue #3723).
export function scorePattern(
  pattern: RhythmPattern,
  level: LearnLevel,
  tapsMs: number[],
  input: RhythmInput = 'tap',
): PatternResult {
  const { bpm, windowMs } = rhythmLevelParams[level];
  const onsetsMs = patternOnsetsMs(pattern, bpm);
  const bands = onsetBandsMs(onsetsMs, matchBandMs(level), bpm);
  const assigned: number[][] = onsetsMs.map(() => []);
  const counted: number[] = [];
  for (const tapMs of [...tapsMs].sort((lhs, rhs) => lhs - rhs)) {
    const index = assignTap(tapMs, onsetsMs, bands);
    if (index === undefined) {
      continue; // before the first band opened, on Set
    }
    assigned[index].push(tapMs);
    counted.push(tapMs);
  }
  const beats = onsetsMs.map((onsetMs, index) => beatResult(onsetMs, assigned[index], bands[index], windowMs));
  const onTime = beats.filter(({ status }) => status === BeatStatus.OnTime).length;
  const total = beats.length;
  return {
    pattern,
    beats,
    onTime,
    total,
    headline: patternHeadline(onTime, total),
    hint: patternHint(beats, input),
    color: patternColor(onTime, total),
    tapsMs: counted,
  };
}

export function patternHeadline(onTime: number, total: number): string {
  const count = `${onTime} of ${total}`;
  if (onTime === total) {
    return `Perfect, ${count}`;
  }
  return onTime * 2 >= total ? `Nice, ${count}` : `Keep at it, ${count}`;
}

export function patternColor(onTime: number, total: number): ResultColor {
  if (onTime === total) {
    return 'green';
  }
  return onTime > 0 ? 'yellow' : 'red';
}

const numberWords = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

function numberWord(count: number): string {
  return numberWords[count] ?? count.toString();
}

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm;
}

// How the singer gives a rhythm back: tapped on the big button (Rhythm Echo) or sung into the
// microphone (Sing Rhythm, issue #3723). A sung note's start is timed, aligned and scored exactly as
// a tap is, so everything in this file that says "tap" holds for both; the input only words the hint.
export type RhythmInput = 'tap' | 'voice';

const hintNouns: Record<RhythmInput, { singular: string; plural: string }> = {
  tap: { singular: 'tap', plural: 'taps' },
  voice: { singular: 'note', plural: 'notes' },
};

// Up to two clauses, most frequent first (ties keep the order late, early, missed, extra), joined
// as "One tap was a little late, and one extra tap." (or "One note was ..." for a sung rhythm)
export function patternHint(beats: BeatResult[], input: RhythmInput = 'tap'): string {
  const { singular, plural: many } = hintNouns[input];
  const count = (status: BeatStatus) => beats.filter((beat) => beat.status === status).length;
  const late = count(BeatStatus.Late);
  const early = count(BeatStatus.Early);
  const missed = count(BeatStatus.Missed);
  const extra = beats.reduce((total, beat) => total + beat.extraTaps, 0);
  if (late + early + missed === 0) {
    return 'Every beat on time!';
  }
  const clauses: Array<{ count: number; text: string }> = [
    { count: late, text: `${numberWord(late)} ${plural(late, `${singular} was`, `${many} were`)} a little late` },
    { count: early, text: `${numberWord(early)} ${plural(early, `${singular} was`, `${many} were`)} a little early` },
    { count: missed, text: `you missed ${numberWord(missed)} ${plural(missed, 'beat', 'beats')}` },
    { count: extra, text: `${numberWord(extra)} extra ${plural(extra, singular, many)}` },
  ];
  const chosen = clauses
    .filter((clause) => clause.count > 0)
    .sort((lhs, rhs) => rhs.count - lhs.count) // Array.sort is stable, so ties keep the list order
    .slice(0, 2)
    .map((clause) => clause.text);
  const sentence = chosen.join(', and ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1) + '.';
}

// ---- Session ---------------------------------------------------------------------------------

export interface RhythmSessionResult {
  patterns: PatternResult[];
  onTime: number; // on-time beats across the session
  total: number; // onsets across the session
  score: number; // 0..learnSessionScoreMax, what recordLearnSession receives
  bestStreak: number; // longest run of consecutive patterns with every beat on time
}

// On-time beats over all onsets, scaled to the session score and rounded to nearest
export function sessionScore(results: PatternResult[]): number {
  const total = results.reduce((sum, result) => sum + result.total, 0);
  if (total === 0) {
    return 0;
  }
  const onTime = results.reduce((sum, result) => sum + result.onTime, 0);
  return Math.round((learnSessionScoreMax * onTime) / total);
}

export function bestStreak(results: PatternResult[]): number {
  let best = 0;
  let run = 0;
  for (const result of results) {
    run = result.onTime === result.total ? run + 1 : 0;
    best = Math.max(best, run);
  }
  return best;
}

export function summarizeSession(results: PatternResult[]): RhythmSessionResult {
  return {
    patterns: results,
    onTime: results.reduce((sum, result) => sum + result.onTime, 0),
    total: results.reduce((sum, result) => sum + result.total, 0),
    score: sessionScore(results),
    bestStreak: bestStreak(results),
  };
}

// ---- Warm-up ---------------------------------------------------------------------------------
// The warm-up bar that opens a first session (issue #3523): the singer taps along with clicks on
// the pattern screen until the taps lock in, and the median of the locking offsets is the device's
// tap offset. A singer needs a few clicks to find the beat, so nothing is trusted up front: the
// first bar is heard and not tapped, and from there the clicks simply keep coming, bar after bar,
// until four taps in a row agree. A bad start costs nothing but a few more clicks.
//
// Each measured click owns a window of half a beat either side. Once the clock has passed a
// click's window, the click is matched with the nearest unused tap inside the window, giving an
// offset of tap minus click, or it is a miss. The streak is the run of matched clicks at the tail
// whose offsets agree within warmUpLockSpreadMs: a miss empties it, and a tap that disagrees drops
// the older offsets until the rest agree.
// warmUpLockTaps offsets in the streak lock the run at the moment that click's window closes. A run
// that reaches its last click without locking asks the singer to try once more.
//
// Each click carries a mark, so the screen can tell a tap that is carrying the run from one that
// merely landed in the window: a click whose offset is in the streak is `agreeing`, a click whose
// tap the streak has since dropped is `matched`, a click whose window closed with no tap at all is
// a `miss`, and a click whose window is still open is `pending`.
//
// All of this is pure arithmetic over the timeline, the raw tap stamps and the clock, so the screen
// derives everything it draws by ticking it from the audio clock.

export const warmUpLockTaps = 4; // consecutive agreeing taps that lock the run
export const warmUpLockSpreadMs = 100; // the widest max-minus-min the locking offsets may show
export const warmUpBars = 12; // bars of clicks before the run gives up (about 41 s at 70 bpm)
export const warmUpListenBars = 1; // the first bar is heard, not tapped

// Everything the warm-up schedules, in ms on whatever clock `startMs` is on. `startMs` is the
// moment the run starts; from there one silent lead beat, then a click on every beat for warmUpBars
// bars. The clicks of the listen bar sound under Ready · Set · Go! - the same cue every pattern
// uses, with Go! on the first measured click - and are not matched with anything.
export interface WarmUpTimeline {
  beatMs: number;
  clicksMs: number[]; // every click that may sound
  measuredClicksMs: number[]; // the clicks from the first tapping bar on: clicksMs minus the listen bar
  tapsOpenMs: number; // half a beat before the first measured click; taps before this are ignored
  cueMs: { ready: number; set: number; go: number }; // the last two clicks of the listen bar, then the first measured click
  endMs: number; // half a beat after the last click, when the last window closes
}

export function warmUpTimeline(startMs: number, bpm: number): WarmUpTimeline {
  const beat = beatMs(bpm);
  const listenClicks = warmUpListenBars * rhythmBeatsPerBar;
  const clickCount = warmUpBars * rhythmBeatsPerBar;
  const clicksMs = Array.from({ length: clickCount }, (_click, index) => startMs + (index + 1) * beat);
  const measuredClicksMs = clicksMs.slice(listenClicks);
  return {
    beatMs: beat,
    clicksMs,
    measuredClicksMs,
    tapsOpenMs: measuredClicksMs[0] - beat / 2,
    cueMs: {
      ready: clicksMs[listenClicks - 2],
      set: clicksMs[listenClicks - 1],
      go: measuredClicksMs[0],
    },
    endMs: clicksMs[clicksMs.length - 1] + beat / 2,
  };
}

// One per measured click, in click order; see the mark vocabulary above
export type WarmUpMark = 'pending' | 'agreeing' | 'matched' | 'miss';

export type WarmUpOutcome = { kind: 'locked'; offsetMs: number } | { kind: 'retry' };

export interface WarmUpProgress {
  marks: WarmUpMark[]; // one per measured click
  streakMs: number[]; // the agreeing offsets at the tail, at most warmUpLockTaps (the run locks there)
  soundedIndex: number | undefined; // index into clicksMs of the last click at or before nowMs
  outcome?: WarmUpOutcome;
}

// The tap in `unused` nearest to `clickMs` and no further than `halfBeatMs` from it, or undefined
function nearestUnusedTap(clickMs: number, unused: number[], halfBeatMs: number): number | undefined {
  let nearest: number | undefined;
  for (const tapMs of unused) {
    const distance = Math.abs(tapMs - clickMs);
    if (distance <= halfBeatMs && (nearest === undefined || distance < Math.abs(nearest - clickMs))) {
      nearest = tapMs;
    }
  }
  return nearest;
}

function spreadMs(values: number[]): number {
  return Math.max(...values) - Math.min(...values);
}

// `tapsMs` are raw tap stamps, deliberately not aligned by any stored offset: the offset is what
// the run measures.
export function warmUpProgress(timeline: WarmUpTimeline, tapsMs: number[], nowMs: number): WarmUpProgress {
  const halfBeat = timeline.beatMs / 2;
  const unused = [...tapsMs];
  const marks: WarmUpMark[] = timeline.measuredClicksMs.map(() => 'pending');
  // the listen bar included, so the screen can light its clicks too
  const soundedIndex = lastReachedIndex(timeline.clicksMs, nowMs);
  // the streak carries each offset's click with it, so the clicks it holds can be marked below
  let streak: Array<{ index: number; offsetMs: number }> = [];
  let outcome: WarmUpOutcome | undefined;
  for (const [index, clickMs] of timeline.measuredClicksMs.entries()) {
    if (clickMs + halfBeat > nowMs) {
      break; // this click's window is still open, and so is every window after it
    }
    const nearest = nearestUnusedTap(clickMs, unused, halfBeat);
    if (nearest === undefined) {
      marks[index] = 'miss';
      streak = [];
      continue;
    }
    unused.splice(unused.indexOf(nearest), 1);
    marks[index] = 'matched';
    streak.push({ index, offsetMs: nearest - clickMs });
    while (streak.length > 1 && spreadMs(streak.map((tap) => tap.offsetMs)) > warmUpLockSpreadMs) {
      streak.shift();
    }
    if (streak.length >= warmUpLockTaps) {
      // the streak holds warmUpLockTaps offsets here, so the median is always defined
      const offsetMs = median(streak.map((tap) => tap.offsetMs)) ?? 0;
      outcome = { kind: 'locked', offsetMs };
      break; // the clicks after the locking one never sound
    }
  }
  for (const { index } of streak) {
    marks[index] = 'agreeing'; // the offsets still agreeing at the tail, the ones the pips count
  }
  if (!outcome && nowMs >= timeline.endMs) {
    outcome = { kind: 'retry' };
  }
  return { marks, streakMs: streak.map((tap) => tap.offsetMs), soundedIndex, outcome };
}

// ---- Tap alignment ---------------------------------------------------------------------------
// What the warm-up measures and the scoring applies: the median of the locking offsets is stored
// per device, and every tap is shifted by it before it is compared with an onset.

export function median(values: number[]): number | undefined {
  if (values.length === 0) {
    return undefined;
  }
  const sorted = [...values].sort((lhs, rhs) => lhs - rhs);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

// A tap stamped at `tapMs` on a device whose taps read `offsetMs` late was meant for this time
export function alignTap(tapMs: number, offsetMs: number): number {
  return tapMs - offsetMs;
}
